import { MIN_COVERAGE_DISTRIBUTION_SHIFT } from "./runsSummaryConstants"
import type { CoverageComparison, RunsPageSummaryRow } from "./runsSummaryTypes"

function incrementCount(counts: Map<string, number>, key: string): void {
  counts.set(key, (counts.get(key) ?? 0) + 1)
}

function countRowValues(
  rows: RunsPageSummaryRow[],
  selector: (row: RunsPageSummaryRow) => string | null | undefined,
): Map<string, number> {
  const counts = new Map<string, number>()
  rows.forEach((row) => {
    incrementCount(counts, selector(row) ?? "__missing__")
  })
  return counts
}

function countCoverageSignatures(
  rows: RunsPageSummaryRow[],
): Map<string, number> {
  const counts = new Map<string, number>()
  rows.forEach((row) => {
    incrementCount(
      counts,
      [
        row.agent_id ?? "__missing__",
        row.test_set_id ?? "__missing__",
        row.persona_id_id ?? "__missing__",
        row.model_type ?? "__missing__",
      ].join("|"),
    )
  })
  return counts
}

function haveSameKeys(
  first: Map<string, number>,
  second: Map<string, number>,
): boolean {
  if (first.size !== second.size) {
    return false
  }

  return [...first.keys()].every((key) => second.has(key))
}

function totalCount(counts: Map<string, number>): number {
  return [...counts.values()].reduce((sum, count) => sum + count, 0)
}

function hasCoverageMixChanged(
  currentCounts: Map<string, number>,
  previousCounts: Map<string, number>,
): boolean {
  if (!haveSameKeys(currentCounts, previousCounts)) {
    return true
  }

  if (currentCounts.size <= 1) {
    return false
  }

  const currentTotal = totalCount(currentCounts)
  const previousTotal = totalCount(previousCounts)
  if (currentTotal === 0 || previousTotal === 0) {
    return false
  }

  return [...currentCounts.keys()].some((key) => {
    const currentShare = (currentCounts.get(key) ?? 0) / currentTotal
    const previousShare = (previousCounts.get(key) ?? 0) / previousTotal
    return (
      Math.abs(currentShare - previousShare) >= MIN_COVERAGE_DISTRIBUTION_SHIFT
    )
  })
}

export function buildCoverageComparison(
  currentRows: RunsPageSummaryRow[],
  previousRows: RunsPageSummaryRow[],
): CoverageComparison {
  const signaturesChanged = hasCoverageMixChanged(
    countCoverageSignatures(currentRows),
    countCoverageSignatures(previousRows),
  )
  if (!signaturesChanged) {
    return { isLikeForLike: true, changedDimensions: [] }
  }

  const dimensions: Array<{
    label: string
    selector: (row: RunsPageSummaryRow) => string | null | undefined
  }> = [
    { label: "agent", selector: (row) => row.agent_id },
    { label: "test set", selector: (row) => row.test_set_id },
    { label: "persona", selector: (row) => row.persona_id_id },
    { label: "simulator", selector: (row) => row.model_type },
  ]
  const changedDimensions = dimensions
    .filter(({ selector }) =>
      hasCoverageMixChanged(
        countRowValues(currentRows, selector),
        countRowValues(previousRows, selector),
      ),
    )
    .map(({ label }) => label)

  return {
    isLikeForLike: false,
    changedDimensions:
      changedDimensions.length > 0 ? changedDimensions : ["coverage"],
  }
}
